import axios from 'axios';
import * as z from 'zod';
import z__default, { z as z$1 } from 'zod';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import CryptoJS from 'crypto-js';

class SdkError extends Error {
  constructor(message, body) {
    super(message);
    this.name = "SdkError";
    this.data = body;
    Object.setPrototypeOf(this, SdkError.prototype);
  }
}

var checkModule = (httpClient) => ({
  checkUnique: async (type, value) => {
    const { data } = await httpClient.get(`/v2/client/unique?type=${type}&value=${value}`).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

const date = dayjs;
date.extend(utc);
date.extend(timezone);
date.extend(advancedFormat);
date.extend(relativeTime);
date.extend(localizedFormat);
date.extend(customParseFormat);
date.locale("pt-br");
date.tz.setDefault("America/Sao_Paulo");

function convertBRDateToInternational(brDate) {
  if (date(brDate, "DD/MM/YYYY").isValid()) {
    const [day, month, year] = brDate.split("/").map((v) => v.length === 1 ? `0${v}` : v);
    const out = `${year}-${month}-${day}`;
    if (!date(out).isValid())
      throw new Error("Data inv\xE1lida");
    return `${year}-${month}-${day}`;
  }
  if (date(brDate, "YYYY-MM-DD").isValid())
    return brDate;
  throw new Error("Data inv\xE1lida");
}
const cleanNumber = (str) => str.replace(/\D/g, "");
var string = {
  cleanNumber,
  convertBRDateToInternational
};

var Customer;
((Customer2) => {
  Customer2.updateRequestSchema = z__default.object({
    dateBirth: z__default.string().min(1, { message: "Data de nascimento obrigat\xF3ria" }).transform(string.convertBRDateToInternational).superRefine((value, ctx) => {
      if (!date(value, "YYYY-MM-DD", true).isValid()) {
        return ctx.addIssue({
          code: z__default.ZodIssueCode.invalid_date,
          message: "Data de nascimento inv\xE1lida"
        });
      }
      if (date(value).isBefore(date().subtract(100, "years"))) {
        return ctx.addIssue({
          code: z__default.ZodIssueCode.custom,
          message: "Voc\xEA precisa ser mais jovem para continuar :P"
        });
      }
      return z__default.OK;
    }),
    name: z__default.string().superRefine((value, ctx) => {
      if (value.length < 1) {
        return ctx.addIssue({
          code: z__default.ZodIssueCode.custom,
          message: "Nome Obrigat\xF3rio"
        });
      }
      if (value.length < 3) {
        return ctx.addIssue({
          code: z__default.ZodIssueCode.custom,
          message: "Nome muito curto"
        });
      }
      return z__default.OK;
    }),
    phone: z__default.string().min(1, "Telefone obrigat\xF3rio").transform(string.cleanNumber).refine((value) => [11, 12].includes(value.length), "Telefone inv\xE1lido")
  });
})(Customer || (Customer = {}));

var Feedback;
((Feedback2) => {
  Feedback2.feedbackType = z$1.enum([
    "problem_store",
    "problem_sale",
    "problem_app",
    "suggestion_store",
    "support",
    "other"
  ], { required_error: "Por favor, informe o tipo de solicita\xE7\xE3o" });
  Feedback2.feedbackSubtype = z$1.enum([
    "product_without_registration",
    "missing_product",
    "expired_product",
    "product_withdrawal",
    "spoiled_product",
    "product_out_of_stock",
    "problem_with_doors",
    "defective_equipment",
    "other"
  ], { required_error: "Por favor, informe o tipo de problema" });
  Feedback2.type = z$1.enum([
    "suggestion",
    "complaint",
    "reversal",
    "informative",
    "others"
  ]);
  Feedback2.feedbackRequestSchema = z$1.object({
    storeId: z$1.number().min(0),
    feedbackType: Feedback2.feedbackType,
    type: Feedback2.type,
    feedbackSubtype: Feedback2.feedbackSubtype,
    description: z$1.string({ required_error: "Por favor, informe uma descri\xE7\xE3o" }).min(3, "Por favor, informe no m\xEDnimo 3 caracteres").max(1024)
  });
})(Feedback || (Feedback = {}));

var FeedbackModule = (httpClient) => ({
  /**
   * Envia feedback do cliente.
   * @param args - Os argumentos necessários para enviar o feedback.
   * @returns Uma promessa que resolve com os dados do feedback enviado.
   * @throws {SdkError} - Se houver um erro de validação ou HTTP.
   */
  create: async (args) => {
    const body = await Feedback.feedbackRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.post("/client-feedback", body).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

var Address;
((Address2) => {
  const brazilianStates = [
    "AC",
    "AL",
    "AP",
    "AM",
    "BA",
    "CE",
    "DF",
    "ES",
    "GO",
    "MA",
    "MT",
    "MS",
    "MG",
    "PA",
    "PB",
    "PR",
    "PE",
    "PI",
    "RJ",
    "RN",
    "RS",
    "RO",
    "RR",
    "SC",
    "SP",
    "SE",
    "TO"
  ];
  Address2.addressRequestSchema = z$1.object({
    city: z$1.string({ required_error: "Cidade obrigat\xF3rio" }).min(1, "Cidade obrigat\xF3rio"),
    complement: z$1.string().optional().nullable(),
    country: z$1.string(),
    district: z$1.string({ required_error: "Bairro obrigat\xF3rio" }).min(1, "Bairro obrigat\xF3rio"),
    number: z$1.string({ required_error: "N\xFAmero obrigat\xF3rio" }).min(1, "N\xFAmero obrigat\xF3rio"),
    state: z$1.string({ required_error: "Estado obrigat\xF3rio" }).length(2, { message: "Obrigat\xF3rio 2 caracteres" }).refine((value) => brazilianStates.includes(value.toUpperCase()), {
      message: "Estado inv\xE1lido"
    }),
    street: z$1.string({ required_error: "Endere\xE7o obrigat\xF3rio" }).min(1, "Endere\xE7o obrigat\xF3rio"),
    zipcode: z$1.string({ required_error: "CEP obrigat\xF3rio" }).transform((value) => value.replace(/\D/g, "")).refine((value) => value.length === 8, { message: "CEP deve ter exatamente 8 n\xFAmeros" })
  });
})(Address || (Address = {}));

var AddressModule = (httpClient) => ({
  /**
   * Cadastra um novo endereço.
   * @param args - Os argumentos necessários para cadastrar o endereço.
   * @returns Uma promessa que resolve com os dados do endereço cadastrado.
   * @throws {SdkError} - Se houver um erro de validação ou HTTP.
   */
  create: async (args) => {
    const body = await Address.addressRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.post("/v2/address", body).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Edita um endereço existente.
   * @param id - O ID do endereço a ser editado.
   * @param args - Os argumentos necessários para editar o endereço.
   * @returns Uma promessa que resolve com os dados do endereço editado.
   * @throws {SdkError} - Se houver um erro de validação ou HTTP.
   */
  update: async (id, args) => {
    const body = await Address.addressRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.put(`/v2/address/${id}`, body).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

var Payment;
((Payment2) => {
  Payment2.addPaymentMethod = z$1.object({
    userId: z$1.string(),
    number: z$1.string(),
    verificationValue: z$1.string(),
    firstName: z$1.string(),
    lastName: z$1.string(),
    month: z$1.string(),
    year: z$1.string(),
    cpf: z$1.string(),
    description: z$1.string(),
    reCaptcha: z$1.string().min(1, "Token do reCAPTCHA \xE9 obrigat\xF3rio")
  });
  Payment2.editPaymentMethodSchema = Payment2.addPaymentMethod.extend({
    customerId: z$1.string(),
    paymentMethodId: z$1.string()
  });
  Payment2.deletePaymentMethodSchema = z$1.object({
    customerId: z$1.string(),
    paymentMethodId: z$1.string()
  });
  Payment2.PurchaseRequestSchema = z$1.object({
    couponDiscount: z$1.number().optional(),
    couponName: z$1.string().optional(),
    currentCpf: z$1.string(),
    items: z$1.array(z$1.object({
      listPrice: z$1.number(),
      planogramId: z$1.number(),
      price: z$1.number(),
      productId: z$1.number(),
      productName: z$1.string(),
      quantity: z$1.number()
    })),
    paymentMethod: z$1.enum(["pix", "credit_card", "plugpag_credit", "plugpag_debit", "plugpag_voucher"]),
    paymentMethodId: z$1.string().nullable(),
    status: z$1.enum(["pending"]),
    storeId: z$1.string().optional(),
    pwaId: z$1.string().optional(),
    pwaPush: z$1.string().optional(),
    reCaptcha: z$1.string().min(1, "Token do reCAPTCHA \xE9 obrigat\xF3rio")
  });
  ((PaymentStatusEnum2) => {
    PaymentStatusEnum2["pending"] = "pending";
    PaymentStatusEnum2["paid"] = "paid";
    PaymentStatusEnum2["paid_under_validation"] = "paid_under_validation";
    PaymentStatusEnum2["canceled"] = "canceled";
    PaymentStatusEnum2["in_analysis"] = "in_analysis";
    PaymentStatusEnum2["draft"] = "draft";
    PaymentStatusEnum2["partially_paid"] = "partially_paid";
    PaymentStatusEnum2["refunded"] = "refunded";
    PaymentStatusEnum2["expired"] = "expired";
    PaymentStatusEnum2["in_protest"] = "in_protest";
    PaymentStatusEnum2["chargeback"] = "chargeback";
    PaymentStatusEnum2["approved"] = "approved";
    PaymentStatusEnum2["failed"] = "failed";
  })(Payment2.PaymentStatusEnum || (Payment2.PaymentStatusEnum = {}));
})(Payment || (Payment = {}));

var PaymentModule = (httpClient) => ({
  /**
   * Consulta os métodos de pagamento do usuário.
   * @param params - Objeto onde contém como param o ID da loja (opcional)
   * @returns Uma promessa que resolve com os dados de métodos de pagamentos do usuário
   * @throws {SdkError} - Se houver um erro HTTP.
   */
  get: async (params) => {
    const { data } = await httpClient.get("/payment-methods-store", {
      params
    }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Consulta um método de pagamento do usuário.
   * @param id - ID do método de pagamento
   * @returns Uma promessa que resolve com os dados do método de pagamentos do usuário
   * @throws {SdkError} - Se houver um erro HTTP.
   */
  getOne: async (id) => {
    const { data } = await httpClient.get(`/payment-method/${id}`).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /*
  /* Adiconar um método de pagamento
  */
  addPaymentMethod: async (args) => {
    const params = await Payment.addPaymentMethod.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.post("/payment-methods", params).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /*
  /* Edita um método de pagamento
  */
  put: async (args) => {
    const params = await Payment.editPaymentMethodSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.patch("/payment-methods", params).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /*
   * Exclui um método de pagamento
  */
  deleteMethodPayment: async (args) => {
    const params = await Payment.deletePaymentMethodSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.delete("/payment-methods", {
      params
    }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Define como padrão um método de pagamento pro usuário.
   * @param params - parâmetro a ser passado na requisição
   * @returns Uma promessa que resolve com o status da requisição
   * @throws {SdkError} - Se houver um erro HTTP.
   */
  default: async (params) => {
    const { data } = await httpClient.put("/payment-methods", {}, {
      params
    }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /*
   * Efetua uma compra
  */
  purchase: async (params) => {
    const body = await Payment.PurchaseRequestSchema.parseAsync(params).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.post("/sales", body).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /*
   * Pega todos os métodos de pagamento habilitado para um loja
  */
  getEnabledPaymentMethodsByStore: async (storeId) => {
    const { data } = await httpClient.get(`/store/${storeId}/payment-methods`).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

var customerModule = (httpClient) => ({
  /**
   * Feedback
   */
  feedback: FeedbackModule(httpClient),
  /**
   * Address
   */
  address: AddressModule(httpClient),
  /**
   * Payment
   */
  payment: PaymentModule(httpClient),
  /**
   * Consulta dados do usuário.
   * @returns Uma promessa que resolve com os dados do usuário consultado.
   * @throws {SdkError} - Se houver um erro HTTP.
   */
  read: async () => {
    const { data } = await httpClient.get("/profile").catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Atualiza dados do usuário.
   * @param args - Os argumentos necessários para atualizar os dados do usuário.
   * @returns Uma promessa que resolve com os dados do usuário atualizados.
   * @throws {SdkError} - Se houver um erro de validação ou HTTP.
   */
  update: async (args) => {
    const body = await Customer.updateRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.put("/profile", body).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Deleta dados do usuário.
   * @returns Uma promessa que resolve após a exclusão dos dados do usuário.
   * @throws {SdkError} - Se houver um erro HTTP.
   */
  delete: async () => {
    const response = await httpClient.delete("/users/deleteMyself").catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return response;
  },
  /**
   * Atualiza avatar do usuário.
   * @param body - Os dados do formulário do avatar do usuário.
   * @returns Uma promessa que resolve com os dados do avatar atualizados.
   * @throws {SdkError} - Se houver um erro HTTP.
   */
  updateImage: async (body) => {
    const { data } = await httpClient.put("/profile/image", body, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

var Notification;
((Notification2) => {
  Notification2.searchRequestSchema = z__default.object({
    page: z__default.number().default(1).optional(),
    perPage: z__default.number().default(10).optional(),
    dir: z__default.enum(["asc", "desc"]).default("desc").optional(),
    orderBy: z__default.enum(["readAt", "sentAt", "id"]).default("id").optional()
  });
})(Notification || (Notification = {}));

var notificationModule = (httpClient) => ({
  /**
   * Listar notificações
   *
   * @param {Notification.SearchRequest} args Argumentos da busca
   */
  search: async (args) => {
    const params = await Notification.searchRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.get("/profile/notifications", { params }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Consulta uma notificação pelo Id
   * @param {number} id ID da compra
   */
  get: async (id) => {
    const { data } = await httpClient.get(`/profile/notifications/${id}`);
    return data;
  },
  /**
   * Refresh token
   * @param tokenPush
   */
  refreshToken: async (tokenPush) => {
    const response = await httpClient.post("/push/update", { token: tokenPush }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return response;
  }
});

var Orders;
((Orders2) => {
  Orders2.searchRequestSchema = z__default.object({
    page: z__default.number().optional().default(1),
    perPage: z__default.number().optional().default(10)
  });
  Orders2.cancelRequestSchema = z__default.object({
    saleId: z__default.number(),
    reason: z__default.enum([
      "Produto(s) n\xE3o consta(m) em estoque e desejo cancelar a compra",
      "Comprei o(s) produto(s) errado(s) e desejo cancelar a compra",
      "Desisti da compra e desejo cancel\xE1-la",
      "Produto estragado ou danificado e desejo cancelar a compra",
      "Produto(s) est\xE1(\xE3o) vencido(s) e desejo cancelar a compra",
      "Outro problema com a minha compra"
    ])
  });
  Orders2.GetDiscountCoupon = z__default.object({
    clientId: z__default.string(),
    storeId: z__default.string(),
    value: z__default.number(),
    code: z__default.string(),
    payment: z__default.enum(["credit_card", "debit_card", "pix"]),
    cart: z__default.array(z__default.object({
      productId: z__default.number(),
      value: z__default.number(),
      categoryId: z__default.number()
    }))
  });
  Orders2.SendReceiptSchema = z__default.object({
    email: z__default.string().email(),
    saleId: z__default.number()
  });
  Orders2.GetSalesByPWASchema = z__default.object({
    page: z__default.number().optional(),
    perPage: z__default.number().optional(),
    store: z__default.number().optional().nullable(),
    pwaId: z__default.string().optional()
  });
})(Orders || (Orders = {}));

var orderModule = (httpClient) => ({
  /**
   * Busca as compras do usuário
   *
   * @param {Orders.SearchRequest} args Argumentos da busca
   */
  search: async (args) => {
    const params = await Orders.searchRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.get("/sales/history/paid-pendent", { params }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Consulta uma compra pelo ID
   * @param {number} id ID da compra
   */
  get: async (id) => {
    const { data } = await httpClient.get(`/sales/history/show?salesId=${id}`);
    return data;
  },
  /**
   * Solicita o cancelamento da compra
   * @param {Orders.SaleCancelRequest} args Corpo da requisição
   */
  cancel: async (args) => {
    const body = await Orders.cancelRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.post("/sales/request/cancel", body).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /*
   * Chama a api para obter o valor total do desconto dos produtos com base nos parâmetros enviados
  */
  getCouponDiscount: async (args) => {
    const params = await Orders.GetDiscountCoupon.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.post("/coupons/v2/check-coupon", params).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /*
  * Busca venda a partir de um totem e loja especifica
  */
  getSalesByTotem: async (args) => {
    const params = await Orders.GetSalesByPWASchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.get("/sales/history/pwa", { params }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Envia Recibo para o email
  */
  sendReceipt: async (args) => {
    const params = await Orders.SendReceiptSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.post("/sales/send-confirmation", params).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  checkSale: async (saleId) => {
    const { data } = await httpClient.get(`/sales/reorder/check/${saleId}`).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

const CODES = {
  UNKNOW_ERROR: {
    message: {
      pt: "Erro desconhecido",
      en: "Unknow error"
    }
  },
  USER_ALREADY_EXISTS: {
    message: {
      pt: "O usu\xE1rio j\xE1 existe",
      en: "The user already exists"
    }
  },
  USER_NOT_FOUND: {
    message: {
      pt: "Usu\xE1rio n\xE3o encontrado",
      en: "User not found"
    }
  },
  UNAUTHORIZED: {
    message: {
      pt: "N\xE3o autorizado",
      en: "Unauthorized"
    }
  },
  INVALID_TOKEN: {
    message: {
      pt: "Token inv\xE1lido",
      en: "Invalid token"
    }
  },
  EXPIRED_TOKEN: {
    message: {
      pt: "Token expirado",
      en: "Expired token"
    }
  },
  FORBIDDEN: {
    message: {
      pt: "Proibido",
      en: "Forbidden"
    }
  },
  INTERNAL_SERVER_ERROR: {
    message: {
      pt: "Erro interno do servidor",
      en: "Internal server error"
    }
  },
  NOT_FOUND: {
    message: {
      pt: "N\xE3o encontrado",
      en: "Not found"
    }
  },
  INVALID_CREDENTIALS: {
    message: {
      pt: "Credenciais inv\xE1lidas",
      en: "Invalid credentials"
    }
  },
  INVALID_ID: {
    message: {
      pt: "ID inv\xE1lido",
      en: "Invalid ID"
    }
  },
  INVALID_FIELD: {
    message: {
      pt: "Campo inv\xE1lido",
      en: "Invalid field"
    }
  },
  INVALID_DATA: {
    message: {
      pt: "Dados inv\xE1lidos",
      en: "Invalid data"
    }
  }
};

var Advert;
((Advert2) => {
  Advert2.requestAllAdvertsSchema = z$1.object({
    showIn: z$1.enum(["OUTSTORE", "INSIDESTORE", "SLEEPSCREEN"]),
    storeId: z$1.union([z$1.string(), z$1.number()]).optional()
  }).refine((data) => {
    if (data.showIn === "OUTSTORE") {
      return true;
    }
    return data.storeId !== void 0;
  }, {
    message: "storeId is required when showIn is not OUTSTORE",
    path: ["storeId"]
  });
})(Advert || (Advert = {}));

var AdvertModule = (httpClient) => ({
  /**
   * Busca todos os anúncios de uma loja
   * @param params - Objeto onde contém como param o ID da loja
   * @returns Uma promessa que resolve com os dados dos anúncios da loja
   * @throws {SdkError} - Se houver um erro HTTP.
   */
  get: async (params) => {
    await Advert.requestAllAdvertsSchema.parseAsync(params).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.get("/global-advert/all", {
      params
    }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Busca um anúncio pelo ID
   * @param params - Objeto onde contém como param o `global: booleano`
   * @returns Uma promessa que resolve com os dados de um anúncio
   * @throws {SdkError} - Se houver um erro HTTP.
   */
  getOne: async (id, params) => {
    const { data } = await httpClient.get(`/v2/advert/${id}`, {
      params
    }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /*
   * Busca os anúncios classificados como fora da loja
   * @param params - Objeto onde contém como param o `global: booleano`
   * @returns Uma promessa que resolve com os dados de um anúncio
   * @throws {SdkError} - Se houver um erro HTTP.
  */
  getAdvertOutSideStore: async (params) => {
    const { data } = await httpClient.get("/global-advert", {
      params
    }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

var CategoryModule = (httpClient) => ({
  /**
   * Buscar categorias de uma loja
   */
  get: async (params) => {
    const { storeId, perPage, page } = params;
    const { data } = await httpClient.get(
      `/app/v2/categories/store/${storeId}?page=${page}&perPage=${perPage}`
    ).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

var FavoriteModule = (httpClient) => ({
  /**
   * Buscar lista de planogramas favoritados
   */
  get: async () => {
    const { data } = await httpClient.get("/app/v2/favorites").catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Favoritar um planogram
   *
   * @param {number} planogramId ID do planogram
   */
  favorite: async (planogramId) => {
    const { data } = await httpClient.post(`/profile/favorite/${planogramId}`).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Desfavoritar um planogram
   *
   * @param {number} planogramId ID do planogram
   */
  unfavorite: async (planogramId) => {
    const { data } = await httpClient.post(`/profile/unfavorite/${planogramId}`).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

var Product;
((Product2) => {
  const baseRequestSchema = z__default.object({
    storeId: z__default.number(),
    page: z__default.number().optional().default(1),
    perPage: z__default.number().optional().default(10)
  });
  Product2.productRequestSchema = z__default.object({
    storeId: z__default.number(),
    productId: z__default.number()
  });
  Product2.searchProductByBarcodeSchema = z__default.object({
    storeId: z__default.number(),
    barCode: z__default.number()
  });
  Product2.categoryRequestSchema = baseRequestSchema.extend({
    categoryId: z__default.number()
  });
  Product2.productSearchRequestSchema = z__default.object({
    storeId: z__default.number(),
    productName: z__default.string(),
    page: z__default.number().optional().default(1),
    perPage: z__default.number().optional().default(20)
  });
  Product2.soldoutRequestSchema = baseRequestSchema;
  Product2.buyagainRequestSchema = baseRequestSchema;
})(Product || (Product = {}));

var productModule = (httpClient) => ({
  /**
   * Favoritos
   */
  favorite: FavoriteModule(httpClient),
  /**
   * Categorias
   */
  category: CategoryModule(httpClient),
  /**
   * Anúncios
   */
  advert: AdvertModule(httpClient),
  /**
   * Busca produtos com ofetas de uma loja
   *
   * @param {Product.SoldoutRequest} args Argumentos da busca
   */
  soldout: async (args) => {
    const params = await Product.soldoutRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const {
      storeId,
      perPage,
      page
    } = params;
    const { data } = await httpClient.get(
      `/app/v2/planogram/${storeId}/soldout?page=${page}&perPage=${perPage}`
    ).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Busca produtos do compre de novo de uma loja
   *
   * @param {Product.BuyagainRequest} args Argumentos da busca
   */
  buyagain: async (args) => {
    const params = await Product.buyagainRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const {
      storeId,
      perPage,
      page
    } = params;
    const { data } = await httpClient.get(
      `/app/v2/planogram/${storeId}/buyagain?page=${page}&perPage=${perPage}`
    ).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Busca produtos por categoria de uma loja
   *
   * @param {Product.Category} args Argumentos da busca
   */
  productByCategory: async (args) => {
    const params = await Product.categoryRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const {
      storeId,
      categoryId,
      perPage,
      page
    } = params;
    const { data } = await httpClient.get(
      `/app/v2/planogram?page=${page}&perPage=${perPage}&categoryId=${categoryId}&storeId=${storeId}`
    ).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
  * Busca produtos pelo ID
  *
  * @param {Product.GetRequest} args Argumentos da busca
  */
  get: async (args) => {
    const params = await Product.productRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const {
      storeId,
      productId
    } = params;
    const { data } = await httpClient.get(
      `/product/${productId}/store/${storeId}/planogram`
    ).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
  * Busca produto código de barras
  *
  * @param {Product.ParamsSearchByBarcode} args Argumentos da busca
  */
  searchProductByBarCode: async (args) => {
    const params = await Product.searchProductByBarcodeSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const {
      storeId,
      barCode
    } = params;
    const { data } = await httpClient.get(
      `/store/${storeId}/planogram/barcode/${barCode}`
    ).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Busca produtos a partir de uma lojá
   * @param {Product.ProductSearchRequest} args Argumentos da busca
  */
  searchProductByStore: async (args) => {
    const params = await Product.productSearchRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.get(`client/store/${params.storeId}/planogram/search-paginated/${params.productName}`).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

const getFirstNineDigits = (cpf) => cpf.slice(0, 9).split("").map((item) => Number(item));
const getLastNineDigitsBeforeSecondDigitChecker = (cpf) => cpf.slice(1, 10).split("").map((item) => Number(item));
const handleRestOfSum = (rest) => rest < 2 ? 0 : 11 - rest;
const sumOf = (numArray) => numArray.reduce(
  (acc, number, index) => acc + number * (10 - index),
  0
);
const getFirstDigitChecker = (cpf) => handleRestOfSum(sumOf(getFirstNineDigits(cpf)) % 11);
const getSecondDigitChecker = (cpf) => handleRestOfSum(sumOf(getLastNineDigitsBeforeSecondDigitChecker(cpf)) % 11);
const isValidCPF = (cpf) => {
  let cpfValidation = cpf;
  cpfValidation = cpfValidation.toString();
  cpfValidation = cpfValidation.replace(/[^\d]+/g, "");
  if (cpfValidation.match(/(\d)\1{10}/))
    return false;
  return cpfValidation.slice(-2) === `${getFirstDigitChecker(cpfValidation)}${getSecondDigitChecker(cpfValidation)}`;
};
var cpf = {
  isValid: isValidCPF
};

var Session;
((Session2) => {
  class SessionError extends Error {
    constructor(message) {
      super(JSON.stringify(message));
      this.name = "SessionError";
    }
    get messages() {
      return JSON.parse(this.message);
    }
  }
  Session2.SessionError = SessionError;
  Session2.signinSchema = z.object({
    email: z.string().min(1, "Por favor, informe um email").email("Por favor, informe um email v\xE1lido"),
    password: z.string().min(1, "Por favor, Informe sua senha"),
    reCaptcha: z.string().optional()
  });
  Session2.recoverPasswordSchema = z.object({
    email: z.string().min(1, "Por favor, informe um email").email("Por favor, informe um email v\xE1lido")
  });
  Session2.registerSchema = z.object({
    cpf: z.string({ required_error: "Por favor, informe um CPF" }).min(1, "Por favor, informe um CPF").transform(string.cleanNumber).refine(cpf.isValid, "Por favor, informe um CPF v\xE1lido"),
    dateBirth: z.string().min(1, "Por favor, informe uma data de nascimento"),
    email: z.string().min(1, "Por favor, informe um email").email("Por favor, informe um email v\xE1lido"),
    name: z.string().min(1, "Por favor, informe um nome"),
    phone: z.string().min(1, "Por favor, informe um telefone"),
    profileImage: z.string().optional(),
    provider: z.enum(["apple", "facebook", "google", "email"]),
    providerId: z.string().optional(),
    acceptTerms: z.boolean().refine(Boolean, "Por favor, aceite os termos de uso"),
    id: z.number().optional(),
    reCaptcha: z.string().min(1, "Token do reCAPTCHA \xE9 obrigat\xF3rio")
  });
})(Session || (Session = {}));

var sessionModule = (httpClient) => ({
  /**
   * Retorna o status da sessão do usuário
   * @returns string
   */
  login: async (params, notificationToken = "") => {
    const data = await Session.signinSchema.parseAsync(params);
    const response = await httpClient.post("/sessions/login", {
      ...data,
      notificationToken
    });
    return response;
  },
  /**
   * Efetua o login com o Facebook e retorna o status da sessão do usuário
   * @param token Access token do Facebook
   */
  loginWithFacebook: async (token, notificationToken = "") => {
    const { data: { id, email } } = await axios.get(`https://graph.facebook.com/me?fields=email,id&access_token=${token}`);
    const response = await httpClient.post("/sessions/social-login", {
      socialToken: token,
      email,
      providerId: id,
      provider: "facebook",
      notificationToken
    });
    return response;
  },
  /**
   * Efetua o login com a Apple e retorna o status da sessão do usuário
   * @param identityToken Identity token da Apple
   * @param notificationToken  Token de notificação do usuário
   * @example
   * await sdk.sessions.loginWithApple(appleIdentityToken, 'optionalNotificationToken');
   */
  loginWithApple: async (identityToken, user, notificationToken = "") => {
    const response = await httpClient.post("/sessions/social-login", {
      email: user.email,
      socialToken: identityToken,
      notificationToken,
      providerId: user.id,
      provider: "apple"
    });
    return response;
  },
  /**
   * Efeua o login com o Google e retorna o status da sessão do usuário
   *
   * @param idToken  Identity token do Google
   * @param notificationToken  Token de notificação do usuário
   */
  loginWithGoogle: async (idToken, user, notificationToken = "") => {
    const response = await httpClient.post("/sessions/social-login", {
      email: user.email,
      socialToken: idToken,
      notificationToken,
      providerId: user.id,
      provider: "google"
    });
    return response;
  },
  /**
   * Re-enviar email de confirmação de conta
   */
  resendConfirmation: async ({ email }) => {
    const response = await httpClient.post("/sessions/resend-confirm-account", { email }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return response;
  },
  /**
  /**
   * Retorna o status da sessão do usuário
   */
  status: async () => {
    const response = await httpClient.get("/sessions/get-user");
    return response;
  },
  /**
   * Efetua o logout do usuário
   * @example
   * await sdk.sessions.logout();
   * @returns void
   */
  logout: async () => {
    const response = await httpClient.get("/sessions/logout");
    return response;
  },
  /**
   * Recupera a senha do usuário
   * @param email Email do usuário
   */
  recoverPassword: async (email) => {
    const data = await Session.recoverPasswordSchema.parseAsync({ email });
    const response = await httpClient.post("/sessions/forgot-password", data);
    return response;
  },
  /**
   * Atualiza a senha do usuário
   * @param password Nova senha
   * @param token Token de recuperação
   * @throws {ZodError} Se a senha não for válida
   */
  register: async (params) => {
    const data = await Session.registerSchema.parseAsync(params);
    const response = await httpClient.post("/users/client", data);
    return response;
  }
});

var LockModule = (httpClient) => ({
  /**
   * Buscar locks de uma loja
   */
  get: async (params) => {
    const { storeId, perPage, page } = params;
    const { data } = await httpClient.get(
      `/lock/all?storeId=${storeId}?page=${page}&perPage=${perPage}`
    ).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Abrir uma ou todas trava(s) em uma loja
   */
  open: async (params) => {
    const { storeId } = params;
    const { data } = await httpClient.get(
      `/store/${storeId}/lock${params?.lockId ? `/${params?.lockId}` : ""}`
    ).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

var Store;
((Store2) => {
  Store2.searchRequestSchema = z__default.object({
    latitude: z__default.number(),
    longitude: z__default.number(),
    name: z__default.string().optional(),
    distance: z__default.number().optional().default(999999),
    order: z__default.enum(["distance", "name"]).default("distance"),
    page: z__default.number().optional().default(1),
    perPage: z__default.number().optional().default(10),
    type: z__default.enum(["app", "pwa", "totem"]).optional().default("app"),
    favoriteList: z__default.array(z__default.string()).optional().default([]),
    shopmanagerId: z__default.number().optional()
  });
})(Store || (Store = {}));

var storeModule = (httpClient) => ({
  /**
   * Lock
   */
  lock: LockModule(httpClient),
  /**
   * Busca lojas próximas a localização do usuário, ordenadas por distância
   *
   * @param {Store.SearchRequest} args Argumentos da busca
   */
  search: async (args) => {
    const params = await Store.searchRequestSchema.parseAsync(args).catch((err) => {
      throw new SdkError("VALIDATION_ERROR", err);
    });
    const { data } = await httpClient.get("/app/v2/stores", { params }).catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  },
  /**
   * Consulta uma loja pelo ID
   * @param {number} id ID da loja
   */
  get: async (id) => {
    const { data } = await httpClient.get(`/store/${id}`);
    return data;
  },
  /**
   * Pegar a última compra do usuário
  */
  lastPurchase: async () => {
    const { data } = await httpClient.get("/v2/client/last-purchase").catch((err) => {
      throw new SdkError("HTTP_ERROR", err);
    });
    return data;
  }
});

const hmacHash = (payload, secret) => {
  const hmac = CryptoJS.HmacSHA256(payload, secret);
  return CryptoJS.enc.Base64.stringify(hmac);
};

class ShoppbudAPI {
  constructor(config) {
    this.http = axios.create({
      baseURL: config.url,
      timeout: config.timeout || 30 * 1e3,
      headers: {
        "Content-Type": "application/json",
        ...config.token && { Authorization: `Bearer ${config.token}` },
        ...config.platformAndVersion && { "x-platform": config.platformAndVersion },
        ...config.appVersion && { "x-app-version": config.appVersion },
        ...config.userId && { "x-user-id": config.userId }
      }
    });
    this.http.interceptors.request.use(async (request) => {
      if (config.getTokenFn) {
        const token = await config.getTokenFn();
        request.headers.Authorization = `Bearer ${token}`;
      }
      if (request.data?.reCaptcha) {
        request.headers["x-recaptcha"] = request.data.reCaptcha;
        const { reCaptcha, ...rest } = request.data;
        request.data = rest;
      }
      if (config.getDeviceId) {
        const deviceId = await config.getDeviceId();
        request.headers["x-device-id"] = deviceId;
        request.headers["x-hmac"] = hmacHash(JSON.stringify(request.data), deviceId);
      }
      return request;
    });
    this.session = sessionModule(this.http);
    this.store = storeModule(this.http);
    this.product = productModule(this.http);
    this.order = orderModule(this.http);
    this.customer = customerModule(this.http);
    this.notification = notificationModule(this.http);
    this.check = checkModule(this.http);
  }
  setToken(token) {
    this.http.defaults.headers.Authorization = `Bearer ${token}`;
  }
  clearToken() {
    delete this.http.defaults.headers.Authorization;
  }
  async get(url, params) {
    const { data } = await this.http.get(url, { params });
    return data;
  }
  async post(url, body) {
    const { data } = await this.http.post(url, body);
    return data;
  }
  async put(url, body) {
    const { data } = await this.http.put(url, body);
    return data;
  }
}

export { Address, Advert, CODES, Customer, Feedback, Notification, Orders, Payment, Product, SdkError, Session, ShoppbudAPI, Store };
//# sourceMappingURL=index.es.js.map
